import React, { useState } from "react";
import { Button, Col, Form, message, Row } from "antd";
import { Link, useHistory, useParams } from "react-router-dom";
import { Loader } from "semantic-ui-react";
import { useEmployee, useEmployeePermissions } from "./hooks";

import { useSettingValue } from "../../../utils/Hooks";
import { EmployeeScreenContainer } from "../../../styled-component/EmployeeScreenContainer";
import { useMutation, useQueryClient } from "react-query";
import { AxiosEmployeeAPI } from "../../Api";
import moment from "moment";
import { EditEmployeeForm } from "../EditEmployeeForm";
import { chain } from "lodash";
import { getFormInitialValues } from "./utils";
import { editEmployeeErrorLogs } from "../../utils";

const EditEmployeeScreenComponent = ({ employee, permissions }) => {
  const [form] = Form.useForm();

  const queryClient = useQueryClient();

  const isIntegrationAuthEnabled = useSettingValue("INTEGRATION_AUTH_ENABLED");

  const history = useHistory();

  const [hasChanges, setHasChanges] = useState({});

  const { employeeInitialValues } = getFormInitialValues(employee);

  const handleChange = ({ target: { id, value }, customDiffers }) => {
    const differs = (oldValue, value) => {
      if (oldValue === undefined || oldValue === null) {
        return value !== undefined && value !== null && value !== "";
      }
      return oldValue !== value;
    };
    const diff = customDiffers || differs;
    const newHasChanges = {
      ...hasChanges,
      [id]: diff(employeeInitialValues[id], value),
    };
    setHasChanges(newHasChanges);
  };

  const onSuccess = () => {
    message.success("Funcionário editado com sucesso!");
    history.push("/funcionarios");
  };

  const mutation = useMutation(["editEmployee"], AxiosEmployeeAPI.edit, {
    onError: (err) => editEmployeeErrorLogs(err),
    onSuccess: async () => {
      await queryClient.invalidateQueries(["get_employee", employee.id]);
      await queryClient.invalidateQueries([
        "employeePermission",
        employee?.user?.uuid,
        isIntegrationAuthEnabled,
      ]);
      onSuccess();
    },
  });

  const editEmployee = (employeeId, values) => {
    // systems_permissions hasChanges check would be too complex,
    // so we always pass it
    const onlyChangedValues = {
      systems_permissions: chain(values.systems_permissions)
        .pickBy()
        .keys()
        .value(),
      preferred_channel: values.preferred_channel,
    };
    Object.keys(values).forEach((key) => {
      if (!(key in hasChanges) || !hasChanges[key]) return;

      switch (key) {
        case "birth_date":
          // formats the field
          onlyChangedValues[key] = values.birth_date
            ? moment(values.birth_date).format("YYYY-MM-DD")
            : undefined;
          break;
        case "systems_permissions":
          // assignment done before
          break;
        default:
          onlyChangedValues[key] = values[key];
      }
      onlyChangedValues[key] =
        onlyChangedValues[key] === "" ? null : onlyChangedValues[key];
    });
    mutation.mutate({
      employeeId,
      data: onlyChangedValues,
    });
  };

  return (
    <EmployeeScreenContainer>
      <Row>
        <Col span={24}>
          <EditEmployeeForm
            form={form}
            employeeInitialValues={employeeInitialValues}
            isIntegrationAuthEnabled={isIntegrationAuthEnabled}
            employee={employee}
            permissions={permissions}
            onChange={handleChange}
          />
        </Col>
      </Row>
      <Row justify="end">
        <Col>
          <Link to="/funcionarios">
            <Button
              type="ghost"
              onClick={() => {
                form.resetFields();
              }}
            >
              Cancelar
            </Button>
          </Link>
        </Col>
        <Col offset={1}>
          <Button
            type="primary"
            loading={mutation.isLoading}
            onClick={() => {
              form
                .validateFields()
                .then((values) => {
                  editEmployee(employee.id, values);
                })
                .catch(() =>
                  message.error("Por favor preencha todos campos obrigatórios"),
                );
            }}
          >
            Salvar
          </Button>
        </Col>
      </Row>
    </EmployeeScreenContainer>
  );
};

const EditEmployeeScreen = () => {
  const { employee_id: employeeId } = useParams();

  const { data: employee, isLoading: isLoadingEmployee } =
    useEmployee(employeeId);

  const { data: permissions, isLoading: isLoadingPermissions } =
    useEmployeePermissions(employee);

  if (isLoadingEmployee || isLoadingPermissions) return <Loader active />;

  return (
    <EditEmployeeScreenComponent
      employee={employee}
      permissions={permissions}
    />
  );
};

export default EditEmployeeScreen;
